import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import {
  useUpdateBookCopy,
  useShelfLocations,
  useConditions,
  useBulkStatusUpdate,
  type BookCopy,
  type UpdateBookCopyData,
} from '@/hooks/inventory-hooks';
import Drawer from '@/components/admin/components/Drawer';

const inputCls = "w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-lg text-sm text-[var(--lib-ink)] placeholder:text-[var(--lib-ink-muted)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-[var(--lib-gold)]/15 bg-transparent";
const labelCls = "block font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-1.5";

// Validation schemas
const updateBookCopySchema = z.object({
  status: z.enum(['available', 'borrowed', 'maintenance', 'missing', 'withdrawn', 'reserved', 'in_transit']),
  locationId: z.number().nullable().optional(),
  conditionId: z.number(),
  internalNotes: z.string().optional(),
});

type UpdateBookCopyFormData = z.infer<typeof updateBookCopySchema>;

interface UpdateBookCopyFormProps {
  bookCopy: BookCopy | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const UpdateBookCopyForm: React.FC<UpdateBookCopyFormProps> = ({
  bookCopy,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { toast } = useToast();
  const updateBookCopyMutation = useUpdateBookCopy();

  // Fetch reference data
  const { data: locationsData } = useShelfLocations({ page_size: 100 });
  const { data: conditionsData } = useConditions({ page_size: 100 });

  const locations = locationsData?.results || [];
  const conditions = conditionsData?.results || [];

  const form = useForm<UpdateBookCopyFormData>({
    resolver: zodResolver(updateBookCopySchema),
    defaultValues: {
      status: bookCopy?.status || 'available',
      locationId: bookCopy?.location_info?.id ?? null,
      conditionId: bookCopy?.condition_info?.id || 1,
      internalNotes: bookCopy?.internal_notes || '',
    },
  });

  // Reset form when bookCopy changes
  React.useEffect(() => {
    if (bookCopy) {
      form.reset({
        status: bookCopy.status,
        locationId: bookCopy.location_info?.id ?? null,
        conditionId: bookCopy.condition_info?.id,
        internalNotes: bookCopy.internal_notes || '',
      });
    }
  }, [bookCopy, form]);

  const onSubmit = async (data: UpdateBookCopyFormData) => {
    if (!bookCopy) return;

    try {
      const updatePayload: UpdateBookCopyData = {
        condition_id: data.conditionId,
        internal_notes: data.internalNotes,
      };

      if (data.locationId !== undefined) {
        updatePayload.location_id = data.locationId ?? null;
      }

      const allowedUpdateStatuses: BookCopy['status'][] = [
        'available',
        'maintenance',
        'withdrawn',
        'missing',
        'in_transit',
        'reserved',
      ];

      if (allowedUpdateStatuses.includes(data.status)) {
        updatePayload.status = data.status;
      }

      await updateBookCopyMutation.mutateAsync({
        id: bookCopy.id,
        data: updatePayload,
      });

      toast({
        title: 'Success',
        description: 'Book copy updated successfully',
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating book copy:', error);
      toast({
        title: 'Error',
        description: 'Failed to update book copy',
        variant: 'destructive',
      });
    }
  };

  const statusOptions = [
    { value: 'available', label: 'Available for borrowing' },
    { value: 'reserved', label: 'Reserved for specific user' },
    { value: 'in_transit', label: 'Being moved or processed' },
    { value: 'maintenance', label: 'Under maintenance/repair' },
    { value: 'withdrawn', label: 'Withdrawn from circulation' },
    { value: 'missing', label: 'Missing - location unknown' },
  ];

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title="Update Book Copy"
      footer={
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--lib-ink-muted)] border border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={form.handleSubmit(onSubmit)}
            disabled={updateBookCopyMutation.isPending}
            className="bg-[var(--lib-gold)] hover:brightness-105 disabled:opacity-50 text-[var(--lib-on-gold)] font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
          >
            {updateBookCopyMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Update Copy
          </button>
        </div>
      }
    >
      {bookCopy && (
        <div className="mb-5 p-3 bg-[#F8F7F5] border border-[#E8E4DC] rounded-lg">
          <p className="font-medium text-sm text-[var(--lib-ink)]">{bookCopy.book_info?.title}</p>
          <p className="text-xs text-[var(--lib-ink-muted)] mt-0.5 font-mono">Barcode: {bookCopy.barcode}</p>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelCls}>Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className={inputCls}>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="locationId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelCls}>Location</FormLabel>
                <Select
                  onValueChange={(value) => {
                    if (value === 'none') {
                      field.onChange(null);
                    } else {
                      field.onChange(parseInt(value, 10));
                    }
                  }}
                  value={field.value !== null && field.value !== undefined ? field.value.toString() : 'none'}
                >
                  <FormControl>
                    <SelectTrigger className={inputCls}>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No location assigned</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id.toString()}>
                        {location.label} · {location.library}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="conditionId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelCls}>Condition</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(parseInt(value, 10))}
                  value={field.value?.toString()}
                >
                  <FormControl>
                    <SelectTrigger className={inputCls}>
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {conditions.map((condition) => (
                      <SelectItem key={condition.id} value={condition.id.toString()}>
                        {condition.name} (Score: {condition.score})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="internalNotes"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelCls}>Notes</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Any notes about this copy..."
                    {...field}
                    className={inputCls}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </Drawer>
  );
};

// Quick status update component for bulk operations
interface QuickStatusUpdateProps {
  selectedCopies: number[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const QuickStatusUpdate: React.FC<QuickStatusUpdateProps> = ({
  selectedCopies,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [status, setStatus] = useState<string>('');
  const { toast } = useToast();
  const bulkStatusUpdate = useBulkStatusUpdate();

  const handleBulkUpdate = async () => {
    if (!status || selectedCopies.length === 0) return;

    try {
      await bulkStatusUpdate.mutateAsync({
        copy_ids: selectedCopies,
        new_status: status as BookCopy['status'],
        notes: 'Bulk status update from admin inventory tools',
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Bulk status update failed:', error);
      toast({
        title: 'Error',
        description: 'Failed to update book copies',
        variant: 'destructive',
      });
    }
  };

  const statusOptions = [
    { value: 'available', label: 'Available for borrowing' },
    { value: 'reserved', label: 'Reserved for specific user' },
    { value: 'in_transit', label: 'Being moved or processed' },
    { value: 'maintenance', label: 'Under maintenance/repair' },
    { value: 'withdrawn', label: 'Withdrawn from circulation' },
    { value: 'missing', label: 'Missing - location unknown' },
  ];

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title="Bulk Status Update"
      footer={
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--lib-ink-muted)] border border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleBulkUpdate}
            disabled={bulkStatusUpdate.isPending || !status}
            className="bg-[var(--lib-gold)] hover:brightness-105 disabled:opacity-50 text-[var(--lib-on-gold)] font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
          >
            {bulkStatusUpdate.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Update {selectedCopies.length} Copies
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-[var(--lib-ink-muted)]">
          Update the status of <span className="font-medium text-[var(--lib-ink)]">{selectedCopies.length}</span> selected book copies.
        </p>

        <div>
          <label className={labelCls}>New Status</label>
          <Select onValueChange={setStatus} value={status}>
            <SelectTrigger className={inputCls}>
              <SelectValue placeholder="Select new status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </Drawer>
  );
};
